import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UsersFour, Plus, Trash, X, UserPlus, UserMinus } from "@phosphor-icons/react";

export default function TalentPoolsPage() {
  const { user } = useAuth();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [candidates, setCandidates] = useState([]);
  const [addCandidateId, setAddCandidateId] = useState("");

  useEffect(() => { loadPools(); loadCandidates(); }, []);

  const loadPools = async () => {
    try { const { data } = await api.get("/talent-pools"); setPools(data); }
    catch { toast.error("Failed to load talent pools"); }
    finally { setLoading(false); }
  };

  const loadCandidates = async () => {
    try { const { data } = await api.get("/candidates?limit=200"); setCandidates(data.items || []); }
    catch {}
  };

  const loadPoolDetail = async (poolId) => {
    try { const { data } = await api.get(`/talent-pools/${poolId}`); setShowDetail(data); }
    catch { toast.error("Failed to load pool details"); }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error("Pool name is required"); return; }
    try {
      await api.post("/talent-pools", form);
      toast.success("Talent pool created");
      setShowCreate(false);
      setForm({ name: "", description: "" });
      loadPools();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (poolId) => {
    if (!confirm("Delete this talent pool?")) return;
    try {
      await api.delete(`/talent-pools/${poolId}`);
      toast.success("Talent pool deleted");
      setShowDetail(null);
      loadPools();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleAddCandidate = async () => {
    if (!addCandidateId) { toast.error("Select a candidate"); return; }
    try {
      await api.post(`/talent-pools/${showDetail.id}/candidates`, { candidate_id: addCandidateId });
      toast.success("Candidate added to pool");
      setAddCandidateId("");
      loadPoolDetail(showDetail.id);
      loadPools();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleRemoveCandidate = async (candidateId) => {
    try {
      await api.delete(`/talent-pools/${showDetail.id}/candidates/${candidateId}`);
      toast.success("Candidate removed");
      loadPoolDetail(showDetail.id);
      loadPools();
    } catch { toast.error("Failed to remove"); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-lg" />)}</div>;

  return (
    <div data-testid="talent-pools-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Talent Pools</h1>
          <p className="text-sm text-[#6B7280] mt-1">Group and manage candidate pools</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="create-pool-btn">
          <Plus size={16} /> New Pool
        </Button>
      </div>

      {pools.length === 0 ? (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
          <UsersFour size={48} className="text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[#6B7280]">No talent pools created yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pools.map(pool => (
            <button key={pool.id} onClick={() => loadPoolDetail(pool.id)} className="bg-white rounded-lg border border-[#E5E7EB] p-5 text-left hover:shadow-md transition-shadow" data-testid={`pool-card-${pool.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{pool.name}</h3>
                  {pool.description && <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{pool.description}</p>}
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center">
                  <UsersFour size={20} className="text-[#C5A059]" />
                </div>
              </div>
              <p className="text-xs text-[#C5A059] font-medium mt-3">{pool.candidate_count || 0} candidates</p>
            </button>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 mx-4" data-testid="create-pool-dialog">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-[#0F172A]">Create Talent Pool</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Pool Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="pool-name-input" />
              <textarea className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none" rows={3} placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pool-description-input" />
              <Button onClick={handleCreate} className="w-full bg-[#0F172A] hover:bg-[#1E293B]" data-testid="submit-pool-btn">Create Pool</Button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 mx-4 max-h-[80vh] overflow-y-auto" data-testid="pool-detail-dialog">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#0F172A]">{showDetail.name}</h2>
                {showDetail.description && <p className="text-xs text-[#6B7280]">{showDetail.description}</p>}
              </div>
              <div className="flex gap-2">
                {user?.role === "admin" && <button onClick={() => handleDelete(showDetail.id)} className="text-red-500 hover:text-red-700"><Trash size={18} /></button>}
                <button onClick={() => setShowDetail(null)}><X size={20} className="text-[#6B7280]" /></button>
              </div>
            </div>

            {/* Add candidate */}
            <div className="flex gap-2 mb-4">
              <select className="flex-1 border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" value={addCandidateId} onChange={e => setAddCandidateId(e.target.value)} data-testid="add-candidate-select">
                <option value="">Select Candidate</option>
                {candidates.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <Button onClick={handleAddCandidate} size="sm" className="bg-[#0F172A] gap-1" data-testid="add-candidate-to-pool-btn">
                <UserPlus size={14} /> Add
              </Button>
            </div>

            <h3 className="text-sm font-semibold text-[#0F172A] mb-2">Candidates ({showDetail.candidates?.length || 0})</h3>
            {(showDetail.candidates || []).length === 0 ? (
              <p className="text-sm text-[#6B7280] text-center py-4">No candidates in this pool</p>
            ) : (
              <div className="space-y-2">
                {showDetail.candidates.map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 px-3 bg-[#F9FAFB] rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-[#0F172A]">{c.first_name} {c.last_name}</p>
                      <p className="text-xs text-[#6B7280]">{c.current_role || ""} {c.location ? `| ${c.location}` : ""}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.score > 0 && <span className="text-xs font-medium text-[#C5A059]">{c.score}%</span>}
                      <button onClick={() => handleRemoveCandidate(c.id)} className="text-red-400 hover:text-red-600"><UserMinus size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
